package fr.labodoc.healthProfessional.webApp.pages.medicines

import fr.labodoc.healthProfessional.webApp.data.model.MessageModel
import io.kvision.state.ObservableState
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.datetime.Instant

abstract class MedicinePageViewModel {
  sealed class MedicineUiState {
    data object Loading : MedicineUiState()

    data object Error : MedicineUiState()

    data class Loaded(
      val medicine: Medicine,
      val laboratory: Laboratory?,
      val dosages: List<Dosage>,
      val marketingDocuments: List<MarketingDocument>
    ) : MedicineUiState()
  }

  data class Medicine(
    val id: String,
    val name: String,
    val atcClassification: AtcClassification,
    val logoUrl: String
  ) {
    companion object;

    data class AtcClassification(
      val code: String,
      val name: String,
      val parent: AtcClassification?
    ) {
      val level: Int = when (code.length) {
        1 -> 1
        3 -> 2
        4 -> 3
        5 -> 4
        7 -> 5
        else -> -1
      }

      private fun getLevel(level: Int): AtcClassification? =
        if (this.level == level)
          this
        else
          parent?.getLevel(level)

      fun getLevelOrHigher(level: Int): AtcClassification? =
        if (this.level == level)
          this
        else
          parent?.getLevel(level) ?: parent?.getLevelOrHigher(level - 1)

      val firstLevel: AtcClassification? = getLevel(1)
      val secondLevel: AtcClassification? = getLevel(2)
      val thirdLevel: AtcClassification? = getLevel(3)
      val fourthLevel: AtcClassification? = getLevel(4)
      val fifthLevel: AtcClassification? = getLevel(5)
    }
  }

  data class Laboratory(
    val id: String,
    val name: String,
    val websiteUrl: String,
    val pharmacovigilanceUrl: String
  )

  data class Dosage(
    val cisCode: String,
    val name: String
  )

  data class MarketingDocument(
    val id: String,
    val name: String,
    val url: String,
    val seenLatestVersion: Boolean
  )


  abstract val medicineUiState: ObservableState<MedicineUiState>

  abstract fun tagDocumentAsSeen(id: String)



  sealed class MessageListUiState {
    data object Loading : MessageListUiState()

    data object Error : MessageListUiState()

    data class Loaded(
      val messages: Map<String, Message>
    ) : MessageListUiState()
  }

  sealed class MessageEvent {
    data object SetFavoriteStatusFailed : MessageEvent()
    data class OpenMessage(
      val message: MessageModel
    ): MessageEvent()
  }

  data class Message(
    val id: String,
    val type: Type,
    val title: String,
    val publishedAt: Instant,
    val favorite: Boolean,
    val seen: Boolean
  ) {
    companion object;
    enum class Type {
      FlashInfo,
      Invitation
    }
  }

  abstract val messageListUiState: StateFlow<MessageListUiState>

  abstract val messageListEvents: SharedFlow<MessageEvent>

  abstract fun setMessageFavoriteStatus(messageId: String, favoriteStatus: Boolean)

  abstract fun getMessage(messageId: String)
}
